import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MessageCircle, CheckCircle, Clock, Edit, Save, X, Trash2 } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Layout from './Layout';

interface RoadmapStep {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  estimatedDuration: string;
  estimatedCost?: number;
}

interface Roadmap {
  id: number;
  title: string;
  description: string;
  steps: RoadmapStep[];
  aiGenerated: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse {
  roadmaps: Roadmap[];
}

interface EditingRoadmap {
  id: number;
  title: string;
  description: string;
  steps: RoadmapStep[];
}

const RoadmapPage: React.FC = () => {
  const navigate = useNavigate();
  const [roadmaps, setRoadmaps] = useState<Roadmap[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAIChat, setShowAIChat] = useState(false);
  const [startupCategory, setStartupCategory] = useState('');
  const [aiMessage, setAiMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [editingRoadmap, setEditingRoadmap] = useState<EditingRoadmap | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchRoadmaps();
  }, []);

  const fetchRoadmaps = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse>('/roadmaps');
      setRoadmaps(response.data.roadmaps);
    } catch (error) {
      console.error('Error fetching roadmaps:', error);
      toast.error('Failed to load roadmaps');
    } finally {
      setLoading(false);
    }
  };

  const handleAIGeneration = async () => {
    if (!startupCategory.trim()) {
      toast.error('Please enter your startup category');
      return;
    }
    if (!aiMessage.trim()) {
      toast.error('Please describe your startup');
      return;
    }

    setIsGenerating(true);
    try {
      await api.post('/roadmaps/generate-ai', {
        startupCategory: startupCategory,
        startupDescription: aiMessage
      });
      
      toast.success('AI roadmap generated successfully!');
      setStartupCategory('');
      setAiMessage('');
      setShowAIChat(false);
      fetchRoadmaps(); // Refresh the list
    } catch (error) {
      console.error('Error generating AI roadmap:', error);
      toast.error('Failed to generate AI roadmap');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleStepCompletion = async (roadmapId: number, stepIndex: number) => {
    try {
      const roadmap = roadmaps.find(r => r.id === roadmapId);
      if (!roadmap) return;
      
      const updatedSteps = roadmap.steps.map((step, index) => 
        index === stepIndex ? { ...step, completed: !step.completed } : step
      );

      await api.put(`/roadmaps/${roadmapId}`, {
        steps: updatedSteps
      });

      setRoadmaps(prev => prev.map(r => 
        r.id === roadmapId ? { ...r, steps: updatedSteps } : r
      ));
    } catch (error) {
      console.error('Error updating step:', error);
      toast.error('Failed to update step');
    }
  };

  const startEditing = (roadmap: Roadmap) => {
    setEditingRoadmap({
      id: roadmap.id,
      title: roadmap.title,
      description: roadmap.description,
      steps: [...roadmap.steps]
    });
  };

  const cancelEditing = () => {
    setEditingRoadmap(null);
  };

  const handleDelete = async (roadmapId: number) => {
    if (!window.confirm('Are you sure you want to delete this roadmap? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/roadmaps/${roadmapId}`);
      toast.success('Roadmap deleted successfully');
      fetchRoadmaps(); // Refresh the list
    } catch (error) {
      console.error('Error deleting roadmap:', error);
      toast.error('Failed to delete roadmap');
    }
  };

  const saveRoadmap = async () => {
    if (!editingRoadmap) return;

    setIsSaving(true);
    try {
      await api.put(`/roadmaps/${editingRoadmap.id}`, {
        title: editingRoadmap.title,
        description: editingRoadmap.description,
        steps: editingRoadmap.steps
      });

      setRoadmaps(prev => prev.map(r => 
        r.id === editingRoadmap.id ? {
          ...r,
          title: editingRoadmap.title,
          description: editingRoadmap.description,
          steps: editingRoadmap.steps
        } : r
      ));

      setEditingRoadmap(null);
      toast.success('Roadmap updated successfully!');
    } catch (error) {
      console.error('Error updating roadmap:', error);
      toast.error('Failed to update roadmap');
    } finally {
      setIsSaving(false);
    }
  };

  const updateEditingField = (field: keyof EditingRoadmap, value: string) => {
    if (!editingRoadmap) return;
    setEditingRoadmap(prev => prev ? { ...prev, [field]: value } : null);
  };

  const updateStep = (stepIndex: number, field: keyof RoadmapStep, value: string | boolean | number) => {
    if (!editingRoadmap) return;
    const updatedSteps = editingRoadmap.steps.map((step, index) => 
      index === stepIndex ? { ...step, [field]: value } : step
    );
    setEditingRoadmap(prev => prev ? { ...prev, steps: updatedSteps } : null);
  };

  const addStep = () => {
    if (!editingRoadmap) return;
    const newStep: RoadmapStep = {
      id: Date.now(), // Temporary ID
      title: '',
      description: '',
      completed: false,
      estimatedDuration: '1-2 weeks',
      estimatedCost: 0
    };
    setEditingRoadmap(prev => prev ? { 
      ...prev, 
      steps: [...prev.steps, newStep] 
    } : null);
  };

  const removeStep = (stepIndex: number) => {
    if (!editingRoadmap) return;
    const updatedSteps = editingRoadmap.steps.filter((_, index) => index !== stepIndex);
    setEditingRoadmap(prev => prev ? { ...prev, steps: updatedSteps } : null);
  };

  return (
    <Layout title="Roadmap" showBackButton onBack={() => navigate('/')}>
      <div className="p-4">
        <button
          onClick={() => setShowAIChat(!showAIChat)}
          className="flex items-center text-primary-400 hover:text-primary-300 transition-colors"
        >
          <MessageCircle className="w-5 h-5 mr-1" />
          <span className="text-sm">AI Help</span>
        </button>
      </div>

      {/* AI Chat */}
      {showAIChat && (
        <div className="bg-gray-800 border-b border-gray-700 p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Startup Category
              </label>
              <input
                type="text"
                value={startupCategory}
                onChange={(e) => setStartupCategory(e.target.value)}
                className="input-field w-full"
                placeholder="e.g., SaaS, E-commerce, FinTech, HealthTech..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Describe your startup
              </label>
              <textarea
                value={aiMessage}
                onChange={(e) => setAiMessage(e.target.value)}
                className="input-field w-full h-20 resize-none"
                placeholder="Describe your startup's purpose, goals, and what you're trying to achieve..."
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAIGeneration}
                disabled={isGenerating || !startupCategory.trim() || !aiMessage.trim()}
                className="btn-primary px-4 py-2"
              >
                {isGenerating ? 'Generating...' : 'Generate AI Roadmap'}
              </button>
              <button
                onClick={() => setShowAIChat(false)}
                className="btn-secondary px-4 py-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roadmaps List */}
      <div className="p-4 space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-400">Loading roadmaps...</p>
          </div>
        ) : roadmaps.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 mb-4">No roadmaps yet</p>
            <button
              onClick={() => setShowAIChat(true)}
              className="btn-primary"
            >
              Create your first roadmap
            </button>
          </div>
        ) : (
          roadmaps.map((roadmap) => {
            const isEditing = editingRoadmap?.id === roadmap.id;
            const currentRoadmap = isEditing ? editingRoadmap : roadmap;

            return (
              <div key={roadmap.id} className="card rounded-lg">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    {isEditing ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={currentRoadmap.title}
                          onChange={(e) => updateEditingField('title', e.target.value)}
                          className="input-field text-lg font-semibold"
                          placeholder="Roadmap title"
                        />
                        {/* <textarea
                          value={currentRoadmap.description}
                          onChange={(e) => updateEditingField('description', e.target.value)}
                          className="input-field h-20 resize-none"
                          placeholder="Roadmap description"
                        /> */}
                      </div>
                    ) : (
                      <div>
                        <h3 className="text-lg font-semibold text-white">{roadmap.title}</h3>
                        {roadmap.description && (
                          <p className="text-gray-400 text-sm mt-1">{roadmap.description}</p>
                        )}
                      </div>
                    )}
                    <div className="flex items-center mt-2">
                      {roadmap.aiGenerated && (
                        <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full mr-2">
                          AI Generated
                        </span>
                      )}
                      <span className="text-gray-500 text-xs">
                        {new Date(roadmap.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {isEditing ? (
                      <>
                        <button
                          onClick={saveRoadmap}
                          disabled={isSaving}
                          className="btn-primary px-3 py-1 text-sm flex items-center"
                        >
                          <Save className="w-4 h-4 mr-1" />
                          {isSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="btn-secondary px-3 py-1 text-sm flex items-center"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditing(roadmap)}
                          className="btn-secondary px-3 py-1 text-sm flex items-center"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(roadmap.id)}
                          className="px-3 py-1 text-sm flex items-center text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-colors rounded"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Steps */}
                <div className="space-y-3">
                  {currentRoadmap.steps.map((step, index) => (
                    <div
                      key={step.id}
                      className={`flex items-start p-3 rounded-lg border ${
                        step.completed
                          ? 'bg-green-900/20 border-green-500'
                          : 'bg-gray-700/50 border-gray-600'
                      }`}
                    >
                      {isEditing ? (
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center justify-between">
                            <input
                              type="text"
                              value={step.title}
                              onChange={(e) => updateStep(index, 'title', e.target.value)}
                              className="input-field flex-1 mr-2"
                              placeholder="Step title"
                            />
                            <button
                              onClick={() => removeStep(index)}
                              className="text-red-400 hover:text-red-300 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          <textarea
                            value={step.description}
                            onChange={(e) => updateStep(index, 'description', e.target.value)}
                            className="input-field w-full h-16 resize-none"
                            placeholder="Step description"
                          />
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={step.completed}
                                onChange={(e) => updateStep(index, 'completed', e.target.checked)}
                                className="w-4 h-4 text-primary-600 bg-gray-700 border-gray-600 rounded focus:ring-primary-500 focus:ring-2"
                              />
                              <label className="ml-2 text-sm text-gray-300">
                                Completed
                              </label>
                            </div>
                            <div className="flex items-center">
                              <Clock className="w-3 h-3 text-gray-500 mr-1" />
                              <input
                                type="text"
                                value={step.estimatedDuration}
                                onChange={(e) => updateStep(index, 'estimatedDuration', e.target.value)}
                                className="input-field text-xs w-24"
                                placeholder="Duration"
                              />
                            </div>
                            <div className="flex items-center">
                              <span className="text-gray-500 text-xs mr-1">$</span>
                              <input
                                type="number"
                                value={step.estimatedCost || 0}
                                onChange={(e) => updateStep(index, 'estimatedCost', parseFloat(e.target.value) || 0)}
                                className="input-field text-xs w-28"
                                placeholder="Cost"
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => toggleStepCompletion(roadmap.id, index)}
                            className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mr-3 mt-0.5 transition-colors ${
                              step.completed
                                ? 'bg-green-500 border-green-500'
                                : 'border-gray-400 hover:border-gray-300'
                            }`}
                          >
                            {step.completed && <CheckCircle className="w-5 h-5 text-white" />}
                          </button>
                          <div className="flex-1">
                            <h4 className={`font-medium ${
                              step.completed ? 'text-green-400 line-through' : 'text-white'
                            }`}>
                              {step.title}
                            </h4>
                            <p className={`text-sm mt-1 ${
                              step.completed ? 'text-green-300' : 'text-gray-400'
                            }`}>
                              {step.description}
                            </p>
                            <div className="flex items-center mt-2 space-x-4">
                              <div className="flex items-center">
                                <Clock className="w-3 h-3 text-gray-500 mr-1" />
                                <span className="text-gray-500 text-xs">{step.estimatedDuration}</span>
                              </div>
                              {step.estimatedCost !== undefined && step.estimatedCost !== null && (
                                <div className="flex items-center">
                                  <span className="text-gray-500 text-xs">Cost: </span>
                                  <span className="text-gray-400 text-xs font-medium ml-1">
                                    ${step.estimatedCost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  
                  {isEditing && (
                    <button
                      onClick={addStep}
                      className="w-full p-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-gray-300 hover:border-gray-500 transition-colors flex items-center justify-center"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Step
                    </button>
                  )}
                </div>

                {/* Progress */}
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Progress</span>
                    <span className="text-white">
                      {currentRoadmap.steps.filter(step => step.completed).length} / {currentRoadmap.steps.length}
                    </span>
                  </div>
                  <div className="mt-2 bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${(currentRoadmap.steps.filter(step => step.completed).length / currentRoadmap.steps.length) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Layout>
  );
};

export default RoadmapPage; 